import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trackMarketingEvent } from '../../lib/marketingAnalytics';
import { TRIAL_PATH } from '../../lib/offer';

const textOf = (children) => [].concat(children).filter((c) => typeof c === 'string').join('').replace('→', '').trim();

export const openHashTarget = (id) => {
  const el = document.getElementById(id);
  if (!el) return false;
  if (el.tagName === 'DETAILS') el.open = true;
  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (el.tabIndex === -1) el.focus({ preventScroll: true });
  return true;
};

export const TrackedLink = ({ to, page, section, label, className, children, testId, solution }) => (
  <Link
    to={to}
    className={className}
    data-testid={testId}
    onClick={() => {
      trackMarketingEvent('marketing_cta_click', { page, section, label: label || textOf(children), destination: to });
      if (solution) trackMarketingEvent('solution_navigation_click', { page, section, solution, destination: to });
      const [path, id] = to.split('#');
      if (id && (path || '/') === window.location.pathname) setTimeout(() => openHashTarget(id), 80);
    }}
  >
    {children}
  </Link>
);

const TRIAL = { to: TRIAL_PATH, label: 'Start free trial' };

export const Hero = ({ page, eyebrow, title, lead, support, primary = TRIAL, secondary, demo, image }) => (
  <section className="mk-section" aria-labelledby={`${page}-h1`}>
    <div className={`mk-container mk-hero${image ? '' : ' solo'}`}>
      <div>
        <p className="mk-eyebrow">{eyebrow}</p>
        <h1 id={`${page}-h1`} data-testid={`${page}-hero-title`}>{title}</h1>
        <p className="mk-lead">{lead}</p>
        {support && <p className="mk-muted">{support}</p>}
        <div className="mk-actions">
          <TrackedLink to={primary.to} page={page} section="hero" label={primary.label} className="mk-btn" testId={`${page}-hero-trial-btn`}>{primary.label}</TrackedLink>
          <TrackedLink to={secondary.to} page={page} section="hero" label={secondary.label} className="mk-btn secondary" testId={`${page}-hero-secondary-btn`}>{secondary.label}</TrackedLink>
        </div>
        {demo && <p className="mk-muted" style={{ marginTop: 16, fontSize: 15 }}>Prefer a walkthrough? <TrackedLink to={demo} page={page} section="hero" label="Book a demo" className="mk-link" testId={`${page}-hero-demo-link`}>Book a demo →</TrackedLink></p>}
      </div>
      {image && <img className="mk-shot" src={image.src} alt={image.alt} width="1600" height="1000" sizes="(max-width: 900px) 100vw, 50vw" loading="eager" fetchpriority="high" decoding="async" data-testid={`${page}-hero-image`} />}
    </div>
  </section>
);

export const Section = ({ id, alt, title, intro, children, testId }) => (
  <section id={id} className={`mk-section${alt ? ' alt' : ''}`} data-testid={testId}>
    <div className="mk-container">
      {title && <h2>{title}</h2>}
      {intro && <div className="mk-prose">{[].concat(intro).map((p) => <p key={p}>{p}</p>)}</div>}
      {children}
    </div>
  </section>
);

export const StageGrid = ({ page, stages, cols = 3 }) => (
  <ol className={`mk-grid c${cols}`} style={{ listStyle: 'none', padding: 0 }}>
    {stages.map((s, i) => (
      <li key={s.title} className="mk-card" id={s.id} data-testid={`${page}-stage-${i + 1}`}>
        <p className="mk-num">{String(i + 1).padStart(2, '0')}</p>
        <h3>{s.title}</h3>
        <p>{s.body}</p>
      </li>
    ))}
  </ol>
);

const faqSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items
    .map((q) => ({ name: q.q, text: typeof q.a === 'string' ? q.a : q.text }))
    .filter((q) => q.text)
    .map((q) => ({ '@type': 'Question', name: q.name, acceptedAnswer: { '@type': 'Answer', text: q.text } })),
});

export const FAQ = ({ page, items, openId }) => (
  <div className="mk-faq" data-testid={`${page}-faq`}>
    <Helmet><script type="application/ld+json">{JSON.stringify(faqSchema(items))}</script></Helmet>
    {items.map((q, i) => (
      <details key={q.q} id={q.id} open={openId && q.id === openId ? true : undefined} data-testid={`${page}-faq-item-${i + 1}`}>
        <summary>{q.q}</summary>
        <div>{q.a}</div>
      </details>
    ))}
  </div>
);

const PRICING = { to: '/pricing', label: 'Explore modules and pricing' };

export const Closing = ({ page, title, body, primary = TRIAL, secondary = PRICING }) => (
  <Section testId={`${page}-closing`}>
    <div className="mk-closing">
      <h2>{title}</h2>
      <p className="mk-muted">{body}</p>
      <div className="mk-actions">
        <TrackedLink to={primary.to} page={page} section="closing" label={primary.label} className="mk-btn" testId={`${page}-closing-trial-btn`}>{primary.label}</TrackedLink>
        <TrackedLink to={secondary.to} page={page} section="closing" label={secondary.label} className="mk-btn secondary" testId={`${page}-closing-pricing-btn`}>{secondary.label}</TrackedLink>
      </div>
    </div>
  </Section>
);

export const CardGrid = ({ page, items, cols = 3, prefix = 'capability' }) => (
  <div className={`mk-grid c${cols}`}>
    {items.map((c, i) => (
      <article key={c.title} id={c.id} className="mk-card" style={{ scrollMarginTop: 96 }} data-testid={`${page}-${prefix}-${c.id || i + 1}`}>
        {c.tag && <p className="mk-num">{c.tag}</p>}
        <h3>{c.title}</h3>
        <p>{c.body}</p>
      </article>
    ))}
  </div>
);
